import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserData, deleteUserAccount } from '../services/userService';
import { Download, AlertTriangle, Trash } from 'lucide-react';
import { useDialog } from '../components/ui/Dialog';
import { toast } from '../components/ui/Toast';
import Button from '../components/ui/Button';

const Profile: React.FC = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const { showDialog } = useDialog();
    const [isExporting, setIsExporting] = useState(false);

    const handleExportData = async () => {
        try {
            setIsExporting(true);
            const userData = await getUserData();

            const dataStr = JSON.stringify(userData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = window.URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `winmax-user-data-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: "Success",
                description: "Your data has been exported successfully",
                type: "success"
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to export your data",
                type: "error"
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleDeleteAccount = () => {
        showDialog({
            title: "Delete Account",
            description: "Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data, including licenses.",
            content: (
                <div className="p-4 mt-4 rounded-lg border bg-red-900/20 border-red-900/50">
                    <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
                        <div>
                            <h4 className="text-sm font-medium text-red-500">Warning</h4>
                            <p className="mt-1 text-sm text-red-400">
                                Deleting your account will:
                            </p>
                            <ul className="mt-2 text-sm list-disc list-inside text-red-400">
                                <li>Permanently delete all your personal information</li>
                                <li>Remove all your licenses</li>
                                <li>Cancel any active subscriptions</li>
                                <li>Log you out immediately</li>
                            </ul>
                        </div>
                    </div>
                </div>
            ),
            confirmLabel: "Delete Account",
            cancelLabel: "Cancel",
            variant: "danger",
            onConfirm: async () => {
                try {
                    await deleteUserAccount();
                    logout();
                    navigate('/login');
                    toast({
                        title: "Account Deleted",
                        description: "Your account has been permanently deleted",
                        type: "success"
                    });
                } catch (error) {
                    toast({
                        title: "Error",
                        description: "Failed to delete your account",
                        type: "error"
                    });
                    throw error;
                }
            }
        });
    };

    return (
        <div className="space-y-6 animate-fadeIn">
            <div className="overflow-hidden rounded-lg border shadow-lg bg-dark-800 border-dark-700">
                <div className="px-6 py-5 border-b border-dark-700">
                    <h3 className="text-lg font-medium leading-6 text-dark-100">Profile Settings</h3>
                    <p className="mt-1 text-sm text-dark-400">
                        Manage your account and personal data
                    </p>
                </div>

                <div className="px-6 py-5 space-y-6">
                    <div className="p-4 rounded-lg bg-dark-700/50">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="text-sm font-medium text-dark-100">Account Information</h4>
                                <p className="mt-1 text-sm text-dark-400">
                                    Email: {currentUser?.email}
                                </p>
                                <p className="mt-1 text-sm text-dark-400">
                                    Member since: {new Date(currentUser?.createdAt || '').toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-dark-700">
                        <h4 className="mb-4 text-sm font-medium text-dark-100">Data & Privacy</h4>

                        <div className="space-y-4">
                            <div className="p-4 rounded-lg bg-dark-700/50">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h5 className="text-sm font-medium text-dark-200">Export Personal Data</h5>
                                        <p className="mt-1 text-sm text-dark-400">
                                            Download a copy of your personal data, including your account information and licenses
                                        </p>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        icon={Download}
                                        loading={isExporting}
                                        disabled={isExporting}
                                        onClick={handleExportData}
                                    >
                                        Export Data
                                    </Button>
                                </div>
                            </div>

                            <div className="p-4 rounded-lg bg-dark-700/50">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h5 className="text-sm font-medium text-red-400">Delete Account</h5>
                                        <p className="mt-1 text-sm text-dark-400">
                                            Permanently delete your account and all associated data
                                        </p>
                                    </div>
                                    <Button
                                        variant="danger"
                                        icon={Trash}
                                        onClick={handleDeleteAccount}
                                    >
                                        Delete account
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;